import React, { FC, FormEvent, useState } from 'react';
import api from '../../api';
import FlagValueInput from './FlagValueInput';
import {
  FlagKind,
  FlagRule,
  FlagRuleScope,
  FlagSummary,
  SCOPE_ID_HINTS,
  SCOPE_LABELS,
  apiErrorMessage,
  formatDate,
  formatJson,
  inputToValue,
} from './platformStatsFeatureFlagTypes';

type AuthHeaders = () => Promise<{ headers: { Authorization: string } }>;

interface Props {
  flagKey: string;
  kind: FlagKind;
  rules: FlagRule[];
  scopes: FlagRuleScope[];
  variants: string[];
  readOnly: boolean;
  authHeaders: AuthHeaders;
  onSaved: (updated: FlagSummary) => void;
  onDeleted: (updated: FlagSummary) => void;
}

const SCOPE_ORDER: FlagRuleScope[] = [
  'user',
  'company',
  'organisation',
  'ops_role',
  'email_domain',
];

/**
 * Scoped overrides for one flag. Precedence when several match a request is
 * user > company > organisation > ops role > email domain; a kill switch beats
 * all of them. Saving a rule for an existing (scope, scopeId) replaces it.
 */
const PlatformStatsFeatureFlagRules: FC<Props> = ({
  flagKey,
  kind,
  rules,
  scopes,
  variants,
  readOnly,
  authHeaders,
  onSaved,
  onDeleted,
}) => {
  const [scope, setScope] = useState<FlagRuleScope>(scopes[0] ?? 'user');
  const [scopeId, setScopeId] = useState('');
  const [valueRaw, setValueRaw] = useState(
    kind === 'boolean' ? 'true' : kind === 'variant' ? (variants[0] ?? '') : '',
  );
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState<Record<number, string>>({});

  const url = `/platform-stats/feature-flags/${encodeURIComponent(flagKey)}/rules`;

  const sorted = [...rules].sort(
    (a, b) =>
      SCOPE_ORDER.indexOf(a.scope) - SCOPE_ORDER.indexOf(b.scope) ||
      a.scopeId.localeCompare(b.scopeId),
  );

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scopeId.trim()) {
      setError('Scope id is required');
      return;
    }
    if (!reason.trim()) {
      setError('A reason is required for every change');
      return;
    }
    let value;
    try {
      value = inputToValue(kind, valueRaw);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    setBusy(true);
    try {
      const res = await api.put<FlagSummary>(
        url,
        {
          scope,
          scopeId: scopeId.trim(),
          value,
          note: note.trim() || null,
          reason: reason.trim(),
        },
        await authHeaders(),
      );
      setScopeId('');
      setNote('');
      setReason('');
      onSaved(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save rule'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (rule: FlagRule) => {
    setError(null);
    const r = (deleteReason[rule.id] ?? '').trim();
    if (!r) {
      setError(
        `Enter a reason before removing the ${rule.scope} rule for ${rule.scopeId}`,
      );
      return;
    }
    setBusy(true);
    try {
      const res = await api.delete<FlagSummary>(`${url}/${rule.id}`, {
        ...(await authHeaders()),
        params: { reason: r },
      });
      setDeleteReason((m) => {
        const next = { ...m };
        delete next[rule.id];
        return next;
      });
      onDeleted(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to remove rule'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className='rules'>
      <h4>Rules ({rules.length})</h4>
      <p className='hint'>
        Precedence: user › company › organisation › ops role › email domain. The
        kill switch overrides all rules.
      </p>

      <div className='table-wrap'>
        <table>
          <thead>
            <tr>
              <th>Scope</th>
              <th>Id</th>
              <th>Value</th>
              <th>Note</th>
              <th>Added</th>
              {!readOnly && <th>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className='empty'>
                  No rules.
                </td>
              </tr>
            )}
            {sorted.map((rule) => (
              <tr key={rule.id}>
                <td>{SCOPE_LABELS[rule.scope] ?? rule.scope}</td>
                <td className='mono'>{rule.scopeId}</td>
                <td className='mono'>{formatJson(rule.value)}</td>
                <td>{rule.note ?? ''}</td>
                <td>
                  <div>{formatDate(rule.createdAt)}</div>
                  <div className='muted'>{rule.createdBy ?? ''}</div>
                </td>
                {!readOnly && (
                  <td className='row-actions'>
                    <input
                      type='text'
                      placeholder='reason'
                      aria-label={`Reason for removing ${rule.scope} rule ${rule.scopeId}`}
                      value={deleteReason[rule.id] ?? ''}
                      onChange={(e) =>
                        setDeleteReason((m) => ({
                          ...m,
                          [rule.id]: e.target.value,
                        }))
                      }
                    />
                    <button
                      type='button'
                      className='ghost-btn danger'
                      disabled={busy}
                      onClick={() => void handleDelete(rule)}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <form className='rule-form' onSubmit={handleAdd}>
          <div className='field-grid'>
            <label>
              <span>Scope</span>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as FlagRuleScope)}
              >
                {scopes.map((s) => (
                  <option key={s} value={s}>
                    {SCOPE_LABELS[s] ?? s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Id</span>
              <input
                type='text'
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder={SCOPE_ID_HINTS[scope] ?? ''}
              />
            </label>
            <label>
              <span>Value</span>
              <FlagValueInput
                kind={kind}
                value={valueRaw}
                onChange={setValueRaw}
                variants={variants}
              />
            </label>
            <label>
              <span>Note</span>
              <input
                type='text'
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder='optional, shown next to the rule'
              />
            </label>
          </div>
          <div className='reason-row'>
            <label>
              <span>Reason</span>
              <input
                type='text'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder='Why? (recorded in history)'
                required
              />
            </label>
            <button type='submit' className='primary-btn' disabled={busy}>
              {busy ? 'Saving…' : 'Add / replace rule'}
            </button>
          </div>
        </form>
      )}
      {error && <div className='notice error'>{error}</div>}
    </section>
  );
};

export default PlatformStatsFeatureFlagRules;
